/* eslint-disable camelcase */
import { PostHog } from 'posthog-js';
import { URLs } from '@manifoldxyz/js-ts-utils';
import { InstanceState } from './util';

export type TelemetryProperties = {
  instance_state?: InstanceState; // used by PAGEVIEW
  app?: string; // used by PAGEVIEW
  type?: string; // used by PAGEVIEW
  'link-location'?: string; // used by EXTERNAL_LINK_CLICK
  user_wallet?: string; // used by IDENTIFY
  'checkout-type'?: string; // used by CHECKOUT_BUTTON_CLICK
  tx_type?: 'relay'; // used by REDEEM_TX_ATTEMPT, REDEEM_TX_SUCCESS, REDEEM_TX_FAIL
};

export const TELEMETRY_EVENT_MAP = {
  // gotta have a default pageview event
  PAGEVIEW: `$pageview`,
  // transaction flow events
  REDEEM_TX_ATTEMPT: `claim_widget:transact_flow:redeem_transaction_attempt`,
  REDEEM_TX_SUCCESS: `claim_widget:transact_flow:redeem_transaction_success`,
  REDEEM_TX_FAIL: `claim_widget:transact_flow:redeem_transaction_error`,
  CROSSMINT_TX_ATTEMPT: `claim_widget:transact_flow:crossmint_transaction_attempt`,
  CROSSMINT_TX_SUCCESS: `claim_widget:transact_flow:crossmint_transaction_success`,
  CROSSMINT_TX_FAIL: `claim_widget:transact_flow:crossmint_transaction_error`,
  SECONDARY_PURCHASE_TX_ATTEMPT: `claim_widget:transact_flow:secondary_transaction_attempt`,
  SECONDARY_PURCHASE_TX_SUCCESS: `claim_widget:transact_flow:secondary_transaction_success`,
  SECONDARY_PURCHASE_TX_FAIL: `claim_widget:transact_flow:secondary_transaction_error`,
  USES_DELEGATE_WALLET: `claim_widget:transact_flow:delegate_wallet`,
  SUPPORT_URL_CLICK: `claim_widget:transact_flow:support_url_click`,
  // legacy naming events
  CREATE_YOUR_OWN_BUTTON_CLICK: `claim_widget:create_your_own_flow:button_click`,
  // interaction flow events
  VIEW_ON_MANIFOLD_BUTTON_CLICK: `claim_widget:interaction_flow:view_on_manifold_button_click`,
  EXTERNAL_LINK_CLICK: `claim_widget:interaction_flow:external_link_click`,
  SUBSCRIBE: `claim_widget:interaction_flow:subscribe`,
  CHECKOUT_BUTTON_CLICK: `claim_widget:interaction_flow:checkout_button_click`,
  SECONDARY_CHECKOUT_BUTTON_CLICK: `claim_widget:interaction_flow:secondary_checkout_button_click`,
  CHECKOUT_PREVIEW_CLICKED: `claim_widget:interaction_flow:checkout_preview_clicked`,
  SECONDARY_CHECKOUT_PREVIEW_CLICKED: `claim_widget:interaction_flow:secondary_checkout_preview_clicked`,
  ERC20_SWAP_TX_ATTEMPT: `claim_widget:transact_flow:erc20_swap_transaction_attempt`,
  ERC20_SWAP_TX_SUCCESS: `claim_widget:transact_flow:erc20_swap_transaction_success`,
  ERC20_SWAP_TX_FAIL: `claim_widget:transact_flow:erc20_swap_transaction_error`,
} as const;

export interface IAnalytics {
  identify: (data: { distinctId: string; properties?: TelemetryProperties }) => void;
  capture: (data: {
    event: (typeof TELEMETRY_EVENT_MAP)[keyof typeof TELEMETRY_EVENT_MAP];
    properties?: TelemetryProperties;
  }) => void;
  reset: () => void;
  debug: () => void;
}

/**
 * @important Analytics implementation that just logs data for local development.
 */
export class LocalAnalytics implements IAnalytics {
  identify(data: { distinctId: string; properties?: TelemetryProperties }): void {
    console.warn('identifying user', data);
  }

  capture(data: {
    event: (typeof TELEMETRY_EVENT_MAP)[keyof typeof TELEMETRY_EVENT_MAP];
    properties?: TelemetryProperties;
  }): void {
    console.warn('capturing event', data);
  }

  reset(): void {
    console.warn('resetting analytics');
  }

  debug(): void {
    console.warn('debugging analytics');
  }
}

/**
 * @important Analytics implementation that uses PostHog to send data to the server.
 */
export class PosthogAnalytics implements IAnalytics {
  client: PostHog;

  constructor(client: PostHog) {
    this.client = client;
  }

  identify(data: { distinctId: string; properties?: TelemetryProperties }): void {
    this.client.identify(data.distinctId, data.properties);
  }

  capture(data: {
    event: (typeof TELEMETRY_EVENT_MAP)[keyof typeof TELEMETRY_EVENT_MAP];
    properties?: TelemetryProperties;
  }): void {
    this.client.capture(data.event, data.properties);
  }

  reset(): void {
    this.client.reset();
  }

  /**
   * Use to debug posthog locally.
   * See https://posthog.com/docs/libraries/js#debugging for more info.
   */
  debug(): void {
    this.client.debug();
  }
}

const UTM_SOURCE = 'utm_source=collector_app';

function buildUTMString(utms: { medium: string; campaign: string }): string {
  return (
    '?' +
    UTM_SOURCE +
    '&' +
    Object.entries(utms)
      .map(([key, value]) => `utm_${key}=${value}`)
      .join('&')
  );
}

export function trackedGalleryContractLink(
  networkId: number,
  contractAddress: string,
  utms: {
    medium: string;
    campaign: string;
  },
): string {
  return URLs.getManifoldGalleryContractURL(networkId, contractAddress) + buildUTMString(utms);
}

export function trackedGalleryTokenLink(
  networkId: number,
  contractAddress: string,
  tokenId: string,
  utms: {
    medium: string;
    campaign: string;
  },
): string {
  return (
    URLs.getManifoldGalleryTokenURL(networkId, contractAddress, tokenId) + buildUTMString(utms)
  );
}

export function trackedMarketplaceLink(
  networkId: number,
  utms: { medium: string; campaign: string },
): string {
  return URLs.getManifoldMarketplaceBaseURL(networkId) + buildUTMString(utms);
}
